import React, { createContext, useContext, useState, useEffect } from "react";
import { AxiosHelper } from "../utils/helpers/axios.helper";
import { AuthContextType } from "../interfaces/auth.interface";
import apiClient from "../services/api";
import { getRefreshToken } from "../services/tokenManager";

const AuthContext = createContext<AuthContextType>({
    isAuthenticated: false,
    login: () => { },
    logout: () => { },
    refreshAccessToken: async () => { },
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    useEffect(() => {
        const checkAuth = () => {
            const token =
                localStorage.getItem("accessToken") ||
                sessionStorage.getItem("accessToken");

            if (token) {
                setIsAuthenticated(true);
            } else {
                setIsAuthenticated(false);
            }
        };

        checkAuth();

        window.addEventListener("load", checkAuth);

        window.addEventListener("storage", checkAuth);

        return () => {
            window.removeEventListener("load", checkAuth);
            window.removeEventListener("storage", checkAuth);
        };
    }, []);

    const login = (
        tokens: { accessToken: string; refreshToken: string },
        remember: boolean = false
    ) => {
        const storage = remember ? localStorage : sessionStorage;
        storage.setItem("accessToken", tokens.accessToken);
        storage.setItem("refreshToken", tokens.refreshToken);
        setIsAuthenticated(true);
    };

    const logout = () => {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        sessionStorage.removeItem("accessToken");
        sessionStorage.removeItem("refreshToken");
        setIsAuthenticated(false);
    };

    const refreshAccessToken = async () => {
        try {
            const refreshToken =
                localStorage.getItem("refreshToken") ||
                sessionStorage.getItem("refreshToken");
            if (!refreshToken) {
                throw new Error("No refresh token available");
            }

            const { data } = await AxiosHelper.instance.post(
                "/api/admin-auth/refresh-token",
                {
                    refreshToken,
                }
            );

            let newTokens;
            if (data.accessToken) {
                newTokens = data;
            } else if (data && data.accessToken) {
                newTokens = data;
            } else {
                console.error("Unexpected response format:", data);
                throw new Error("Unexpected token response format");
            }

            const storage = localStorage.getItem("refreshToken")
                ? localStorage
                : sessionStorage;

            storage.setItem("accessToken", newTokens.accessToken);

            if (newTokens.refreshToken) {
                storage.setItem("refreshToken", newTokens.refreshToken);
            }

            setIsAuthenticated(true);

            return newTokens.accessToken;
        } catch (error) {
            console.error("Token refresh failed:", error);
            logout();
            await apiClient.delete('/auth/logout', {
                data: {
                    refreshToken: getRefreshToken()
                },
            });
            throw error;
        }
    };

    return (
        <AuthContext.Provider
            value={{ isAuthenticated, login, logout, refreshAccessToken }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};
